import Button from "@/components/Button";
import { Checkbox } from "@/components/chadcn/Checkbox";
import { DialogContent, DialogFooter, DialogTitle } from "@/components/chadcn/Dialog";
import { Label } from "@/components/chadcn/Label";
import Textarea from "@/components/chadcn/TextArea";
import Dialog from "@/components/Dialog";
import { NumberInput, SelectInput } from "@/components/InputFields";
import Spinner from "@/components/Spinner";
import Toast from "@/components/Toast";
import { useAddClinicalNoteMutation, usePrescribingCaregiversQuery } from "@/data/queries";
import { Caregiver } from "@/model/Caregiver";
import ClinicalProtocolType from "@/model/ClinicalProtocolType";
import { Organization, ReportDeliveryMethod } from "@/model/Organization";
import { Duration } from "@js-joda/core";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

type FormValues = {
  sendToEmr?: boolean;
  sendToFax?: boolean;
  resetDaysSinceReview?: boolean;
  assignToCaregiverId?: string;
  timeSpentInMinutes: number;
  livePatientInteraction?: boolean;
  notes: string;
};

type LiveInteractionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  protocolType: ClinicalProtocolType;
  organization: Organization;
  patientId: string | undefined;
  prescribingCaregiver: Caregiver;
};

export const LiveInteractionDialog = ({
  open,
  onOpenChange,
  patientId,
  protocolType,
  prescribingCaregiver,
  organization,
}: LiveInteractionDialogProps) => {
  const {
    mutateAsync: addClinicalNote,
    isPending: isAddingClinicalNote,
    isError: isAddClinicalNoteError,
  } = useAddClinicalNoteMutation({ patientId: patientId ?? "" });

  const {
    data: caregivers = [],
    isLoading: isLoadingCaregivers,
    isError: isErrorCaregivers,
  } = usePrescribingCaregiversQuery();

  const deliveryMethods = organization.reportDeliveryMethods;
  const canSendReport = deliveryMethods.length > 0;
  const isEmrEnabled = deliveryMethods.includes("emr");
  const isFaxEnabled = deliveryMethods.includes("fax");

  const defaultValues: FormValues = {
    sendToEmr: isEmrEnabled ? undefined : false,
    sendToFax: isFaxEnabled ? undefined : false,
    resetDaysSinceReview: false,
    assignToCaregiverId: isEmrEnabled ? prescribingCaregiver?.id : undefined,
    timeSpentInMinutes: 5,
    livePatientInteraction: true,
    notes: "",
  };

  const { register, handleSubmit, setValue, watch, reset } = useForm<FormValues>({
    defaultValues,
  });

  const {
    sendToEmr,
    sendToFax,
    resetDaysSinceReview,
    assignToCaregiverId,
    timeSpentInMinutes,
    livePatientInteraction,
    notes,
  } = watch();

  useEffect(() => {
    if (resetDaysSinceReview == undefined && (sendToEmr !== undefined || sendToFax !== undefined)) {
      setValue("resetDaysSinceReview", sendToEmr === true || sendToFax === true);
    }
  }, [resetDaysSinceReview, sendToEmr, sendToFax, setValue]);

  useEffect(() => {
    if (prescribingCaregiver) {
      setValue("assignToCaregiverId", prescribingCaregiver.id);
    }
  }, [prescribingCaregiver, setValue]);

  const incrementTimeSpent = () => {
    setValue("timeSpentInMinutes", timeSpentInMinutes + 1);
  };

  const decrementTimeSpent = () => {
    setValue("timeSpentInMinutes", Math.max(0, timeSpentInMinutes - 1));
  };

  const chosenDeliveryMethods = deliveryMethods.filter(
    (method) => (method === "emr" && sendToEmr) || (method === "fax" && sendToFax)
  );

  const displayReportQueuedToast = (methods: ReportDeliveryMethod[]) => {
    const description = methods.includes("emr")
      ? methods.includes("fax")
        ? "The report is being generated and will be sent to the EMR and faxed shortly"
        : "The report is being generated and will be sent to the EMR shortly"
      : "The report is being generated and will be faxed shortly";
    toast.custom((toastId) => <Toast description={description} toastId={toastId} />, {
      duration: 7000,
    });
  };

  const onSubmitInternal = async (form: FormValues) => {
    const methods = chosenDeliveryMethods;
    const sendReport = methods.length > 0;
    try {
      await addClinicalNote({
        type: protocolType,
        timeSpent: Duration.ofMinutes(form.timeSpentInMinutes),
        actionType: form.resetDaysSinceReview ? "review" : "note",
        livePatientInteraction: form.livePatientInteraction ?? false,
        notes: form.notes,
        assignToCaregiverId:
          isEmrEnabled && form.sendToEmr && form.assignToCaregiverId
            ? form.assignToCaregiverId
            : null,
        deliveryMethods: methods,
      });
      handleOpenChange(false);
      if (sendReport) {
        displayReportQueuedToast(methods);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (open) {
      reset(defaultValues);
    }
  };

  const canSubmit =
    !isNaN(timeSpentInMinutes) &&
    timeSpentInMinutes >= 0 &&
    (!isEmrEnabled || sendToEmr !== undefined) &&
    (!isFaxEnabled || sendToFax !== undefined) &&
    livePatientInteraction !== undefined &&
    resetDaysSinceReview !== undefined &&
    notes.trim() !== "";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex min-w-[720px] max-w-[10%] flex-col gap-y-4 bg-white">
        <DialogTitle>Log Live Interaction</DialogTitle>
        <form
          key={`${organization.id}-${canSendReport}`}
          className="flex flex-col gap-y-4"
          onSubmit={handleSubmit(onSubmitInternal)}
        >
          {canSendReport && (
            <>
              {isEmrEnabled && (
                <div className="flex flex-col">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-primary/90">
                      Do you want to send the report to the patient's chart in the EMR?{" "}
                      <span className="text-red-500">*</span>
                    </span>
                    <YesNoCheckboxes
                      disabled={isAddingClinicalNote}
                      checked={sendToEmr}
                      onCheckedChange={(checked) => setValue("sendToEmr", checked)}
                    />
                  </div>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      sendToEmr === true ? "mt-4 max-h-32 opacity-100" : "mt-0 max-h-0 opacity-0"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-primary/90">Assign to provider</span>
                      <SelectInput
                        className="max-w-72"
                        value={assignToCaregiverId || ""}
                        options={[
                          { value: "", title: "None" },
                          ...(isErrorCaregivers
                            ? []
                            : caregivers.map((caregiver) => ({
                                value: caregiver.id,
                                title: caregiver.displayName,
                              }))),
                        ]}
                        onChange={(value) => setValue("assignToCaregiverId", value)}
                        disabled={isAddingClinicalNote || isLoadingCaregivers}
                      />
                      {isLoadingCaregivers && <Spinner className="ms-2" />}
                      {isErrorCaregivers && (
                        <span className="text-sm text-red-500">Failed to load providers</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {isFaxEnabled && (
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-primary/90">
                    Do you want to send the report to the clinic via fax?{" "}
                    <span className="text-red-500">*</span>
                  </span>
                  <YesNoCheckboxes
                    disabled={isAddingClinicalNote}
                    checked={sendToFax}
                    onCheckedChange={(checked) => setValue("sendToFax", checked)}
                  />
                </div>
              )}
              <hr className="border-divider" />
            </>
          )}
          <div className="flex flex-col">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-primary/90">
                Do you want to reset the "Days since review"?{" "}
                <span className="text-red-500">*</span>
              </span>
              <YesNoCheckboxes
                disabled={isAddingClinicalNote}
                checked={resetDaysSinceReview}
                onCheckedChange={(checked) => setValue("resetDaysSinceReview", checked)}
              />
            </div>
            <span className="mt-1 text-sm italic text-primary/60">
              Selecting yes will reset "days since review" for this patient in the Overview
            </span>
          </div>
          <hr className="border-divider" />
          <div className="flex items-center justify-between">
            <Label htmlFor="review-duration-input" className="text-base font-semibold">
              Time spent (minutes) <span className="text-red-500">*</span>
            </Label>
            <div className="flex gap-2">
              <Button
                variant="neutral"
                type="button"
                className="material-icons size-10 p-0 text-slate-700"
                onClick={decrementTimeSpent}
                disabled={isAddingClinicalNote}
              >
                remove
              </Button>
              <NumberInput
                id="review-duration-input"
                className="h-10 w-14 border-gray-300 text-center font-medium"
                {...register("timeSpentInMinutes", { valueAsNumber: true })}
              />
              <Button
                variant="neutral"
                type="button"
                className="material-icons size-10 p-0 text-slate-700"
                onClick={incrementTimeSpent}
                disabled={isAddingClinicalNote}
              >
                add
              </Button>
            </div>
          </div>
          <hr className="border-divider" />
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col">
              <Label className="text-base font-semibold">
                Did you speak with the patient? <span className="text-red-500">*</span>
              </Label>
              <span className="text-primary/90">(e.g. by phone)</span>
            </div>
            <YesNoCheckboxes
              checked={livePatientInteraction}
              disabled={true} // Since the user is logging a live interaction with the patient, this is locked to true
              onCheckedChange={() => {}} // No-op since the checkbox is locked to true
            />
          </div>
          <div>
            <div className="mb-2 flex flex-row justify-between">
              <Label htmlFor="review-notes-input" className="mt-2 text-xl font-bold">
                Notes <span className="text-red-500">*</span>
              </Label>
            </div>
            <Textarea
              id="review-notes-input"
              className="h-32 border-gray-300"
              {...register("notes")}
            />
          </div>
          {isAddClinicalNoteError && (
            <p className="mt-1 text-red-600">
              There was an error submitting the note. Please contact Bloomlife Support, or try again
              later.
            </p>
          )}
          <DialogFooter>
            <div className="flex gap-2">
              <Button
                variant="neutral"
                type="button"
                onClick={() => handleOpenChange(false)}
                className="w-fit border-none bg-primary/10"
                disabled={isAddingClinicalNote}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={!canSubmit || isAddingClinicalNote}
                className="w-fit"
              >
                Submit
                {isAddingClinicalNote && <Spinner className="ms-2 size-4 border-2" />}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

type YesNoCheckboxesProps = {
  checked?: boolean;
  disabled?: boolean;
  onCheckedChange: (isChecked: boolean) => void;
};

const YesNoCheckboxes = ({ checked, onCheckedChange, disabled }: YesNoCheckboxesProps) => (
  <div className="flex items-center gap-2">
    <Checkbox
      checked={checked === undefined ? false : checked}
      onCheckedChange={(checkedState) => onCheckedChange(checkedState === true)}
      disabled={disabled}
    />
    <span className="me-4 text-primary/90">Yes</span>
    <Checkbox
      checked={checked === undefined ? false : !checked}
      onCheckedChange={(checkedState) => onCheckedChange(checkedState === false)}
      disabled={disabled}
    />
    <span className="text-primary/90">No</span>
  </div>
);
